/**
 * Taskbar REC chip (cdb_webos#393) — the GLOBAL recording status.
 *
 * Rendered in the taskbar's right section, so an active audio recording is
 * visible regardless of window focus/minimization (the local waveform/timer
 * indicators die with the window). Shows a pulsing red dot + elapsed time of
 * the oldest active recording (+n when several run); clicking focuses the
 * recording window.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { useRecordingStatus } from '@/contexts/RecordingStatusContext'
import { useWindows } from '@/hooks/useWindows'
import { formatDuration } from '@/utils/audioRecordingUtils'

export function TaskbarRecordingIndicator() {
  const { t } = useTranslation()
  const { recordings } = useRecordingStatus()
  const { focusWindow } = useWindows()
  const [, forceTick] = useState(0)

  // 1s ticker for the elapsed time — only while something records.
  useEffect(() => {
    if (recordings.length === 0) return
    const id = window.setInterval(() => forceTick((n) => n + 1), 1000)
    return () => window.clearInterval(id)
  }, [recordings.length])

  if (recordings.length === 0) return null

  const oldest = recordings.reduce((a, b) => (a.startedAt <= b.startedAt ? a : b))
  const elapsedS = Math.max(0, Math.floor((Date.now() - oldest.startedAt) / 1000))

  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      onClick={() => oldest.windowId && focusWindow(oldest.windowId)}
      title={t('recordingConsent.globalStatusHint')}
      className="flex h-7 items-center gap-1.5 rounded-full bg-red-600/90 px-2.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-red-600"
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
      </span>
      REC {formatDuration(elapsedS)}
      {recordings.length > 1 && <span className="opacity-80">+{recordings.length - 1}</span>}
    </motion.button>
  )
}
